<template>
	<div class="slider-wrapper">
		<div :class="['input-wrapper', { locked }]"
			:style="{ '--last-allocated-percent': `${lastAllocated}%` }">
			<svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="0 0 57.7 96.3" xml:space="preserve" v-bind:svg-inline="''" v-bind:class="'icon'" v-if="locked" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path class="st0" d="M28.2 0h2.5-2.5zM47.2 38V14C47.2 4.1 39.6.4 30 0 19.8.5 11.7 8.8 11.7 19l-1.4 16.5L0 34.9v57.7l57.7 3.8V38.7L47.2 38zM28.9 11.1c4.4 0 7.9 3.5 7.9 7.9v18.3l-15.7-1V19H21c0-4.3 3.5-7.9 7.9-7.9zm-9.1 67.2l8.4-10.8.8-1.3h-.2c-2 0-3.8-.7-5.1-1.9-.6-.9-1-2.1-1.1-3.7-.3-6.3 8.5-10.1 11-5 1.6 3.2.7 6.8-.2 9.1-.6.4-1.8 1-1.8 1l.9 1.3L40 78.3H19.8z"/></svg>
			<RangeInput
				class="slider"
				v-model.lazy.number="allocated"
				:range="[0, totalBalance]"
				:thumb-percent="thumbPercent"
				:step="stepSize"
				:max="totalBalance"
				:clamp="Math.max(availableToSpend, 0)"
				:after-element-width="`${lastAllocated}%`"
				v-bind="{ disabled: locked }"
				@clamped="playClampSound"
			/>
		</div>
		<InfoPopupToggle
			:active-component="'BudgetInfoPopup'"
			:button-class="'slider-info-btn'"
			v-bind="{ title, ...$attrs }"
		>
			?
		</InfoPopupToggle>
		<div class="label-wrapper">
			<span class="label">{{ title }}</span>
			<span class="amount">{{ formattedAllocated }}</span>
		</div>
	</div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { currencyFormatter } from "@/lib/formatters";
import InfoPopupToggle from "@/components/base/InfoPopupToggle";
import RangeInput from "@/components/game/budget/RangeInput";

export default {
	name: 'BudgetSlider',
	components: {
		InfoPopupToggle,
		RangeInput,
	},
	props: {
		title: {
			type: String,
			required: true,
		},
		id: {
			type: Number,
			required: true,
		},
		locked: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		...mapGetters("game/budgets", ["unallocated"]),
		...mapState({
			totalBalance: (state) => state.game.balance.amount,
			stepSize: (state) => state.global.api.data.config.rounding_step_size,
		}),
		isInfoOpen() {
			return this.$store.getters["global/modal/isActiveKey"](this.id);
		},
		allocated: {
			get() { return this.$store.getters[`game/budgets/list`][this.id].allocated; },
			set(amount) { this.$store.dispatch(`game/budgets/${this.id}/allocate`, { amount }); },
		},
		lastAllocated() {
			return this.$store.state.game.budgets[this.id].lastPercentAllocated * 100;
		},
		formattedAllocated() {
			return currencyFormatter.format(this.allocated);
		},
		availableToSpend() {
			return (this.unallocated + this.allocated);
		},
		thumbPercent() {
			return window.screen.width >= 1300 ? 2 : 4;
		},
	},
	methods: {
		playClampSound() {
			this.$store.dispatch("sound/fx/play", { id: "slider_max" });
		},
	},
	watch: {
		allocated(allocated, lastAllocated) {
			if (allocated !== lastAllocated) {
				this.$store.dispatch("sound/fx/play", { id: "slider_tick" });
			}
		},
	},
};
</script>

<style lang="scss" scoped>
	$border-bottom-size: 4px;
	$input-height: 25px + $border-bottom-size;
	$input-width: 225px;

	.slider-wrapper {
		position: relative;
		font-family: $body-font-bold;
		font-size: $font-size-base;
		padding-right: $slider-info-btn-size + 1ch;
		padding-left: 1ch;
		padding-top: 1ch;

		@include mobile {
			font-size: $font-size-x-sm;
			padding-right: 5.5ch;
			margin-bottom: 1ch;
		}
	}

	.input-wrapper {
		height: $input-height;
		min-width: $input-width;
		padding-bottom: $border-bottom-size * 2; // border + margin

		@include mobile {
			min-width: 25vw;
			height: 25px;
		}
	}

	.label {
		color: $off-white;
		letter-spacing: 0.1em;
	}

	.amount {
		color: $body-txt-color;
		float: right;
	}

	.icon {
		display: block;
		height: 50%;
		position: absolute;
		top: 0;
		right: 100%;
	}

	.slider {
		--track-color: transparent;
		--thumb-color: #{$body-txt-color};
		--fill-color: #{$off-white-dark};

		.locked & { --fill-color: #{$body-txt-color}; }

		& /deep/ .input {
			&::-webkit-slider-thumb {
				height: 200%;
				top: -50%;
			}
		}

		& /deep/ .range-thumb {
			border-radius: 50% 50% 50% 50% / 50% 50% 50% 50%;

			&::before {
				content: "";
				background-image: url('~@/assets/images/slider-arrow.svg');
				background-repeat: no-repeat;
				width: 250%;
				height: 60%;
				position: absolute;
				bottom: 100%;
				left: 0;
				transform: translateX(-20%);

				@include mobile {
					left: 50%;
					height: 50%;
					bottom: 110%;
				}

				@include tablet {
					left: 50%;
					height: 60%;
					bottom: 110%;
				}
			}
		}

		// Covers gaps left by raneg thumb border-radius
		& /deep/ .range-fill {
			overflow: hidden;
			&::before {
				content: "";
				width: var(--thumb-width);
				transform: translateX(-50%);
				height: 100%;
				position: absolute;
				top: 0;
				left: var(--thumb-position);
				background: var(--fill-color);
			}
		}

		// Creates the special shape for the bottom border
		& /deep/ .range-fill-wrapper {
			&::before {
				content: "";
				height: 100%;
				width: var(--last-allocated-percent);
				position: absolute;
				top: 0;
				left: 0;
				background: repeating-linear-gradient(-45deg,
					$primary-color,
					$primary-color 3px,
					$primary-color-light 3px,
					$primary-color-light 6px
				);
			}

			&::after {
				content: "";
				width: 100%;
				height: $border-bottom-size;
				margin-top: $border-bottom-size;
				position: absolute;
				left: 0;
				top: 100%;
				border: none;
				border-radius: 50% 50% 50% 50%/50% 50% 50% 50%;
				background: $body-txt-color;
			}
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/game/budget/BudgetSlider.vue